import React, { useCallback, useEffect, useMemo, useState } from "react";
import API from "../config";

const ADMIN_ROLES = ["super_admin", "system_manager"];

const EMPTY_FORM = {
  name: "",
  email: "",
  password: "",
  role_id: "",
};

const normalizeRole = (role = "") => String(role).toLowerCase().replace(/\s+/g, "_");

const Admins = () => {
  const token = localStorage.getItem("token");
  const [admins, setAdmins] = useState([]);
  const [roles, setRoles] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const adminRoles = useMemo(
    () => roles.filter((role) => ADMIN_ROLES.includes(normalizeRole(role.name))),
    [roles]
  );

  const fetchAdmins = useCallback(async () => {
    const res = await fetch(`${API}/users?scope=admins`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error("Failed to load admins");
    const data = await res.json();
    setAdmins(Array.isArray(data) ? data : []);
  }, [token]);

  const fetchRoles = useCallback(async () => {
    const res = await fetch(`${API}/roles`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return;
    const data = await res.json();
    setRoles(Array.isArray(data) ? data : []);
  }, [token]);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        setLoading(true);
        await Promise.all([fetchAdmins(), fetchRoles()]);
      } catch (err) {
        setError(err.message || "Unable to load admins");
      } finally {
        setLoading(false);
      }
    })();
  }, [token, fetchAdmins, fetchRoles]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setError("");
    setShowModal(true);
  };

  const openEdit = (admin) => {
    setEditingId(admin.id);
    setForm({
      name: admin.name || "",
      email: admin.email || "",
      password: "",
      role_id: String(admin.role_id || ""),
    });
    setError("");
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setForm(EMPTY_FORM);
    setEditingId(null);
    setError("");
  };

  const field = (key) => (event) => {
    setForm((prev) => ({ ...prev, [key]: event.target.value }));
  };

  const saveAdmin = async () => {
    setError("");

    if (!form.name.trim() || !form.email.trim() || !form.role_id) {
      setError("Name, email, and role are required");
      return;
    }

    if (!editingId && !form.password) {
      setError("Password is required for a new admin");
      return;
    }

    try {
      setSaving(true);
      const payload = { ...form, company_id: null, device_ids: [] };
      if (editingId && !payload.password) delete payload.password;

      const res = await fetch(`${API}/users${editingId ? `/${editingId}` : ""}`, {
        method: editingId ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (!res.ok) {
        setError(data.message || "Save failed");
        return;
      }

      closeModal();
      await fetchAdmins();
    } catch {
      setError("Server error");
    } finally {
      setSaving(false);
    }
  };

  const deleteAdmin = async (id) => {
    if (!window.confirm("Delete this platform admin?")) return;

    const res = await fetch(`${API}/users/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!res.ok) {
      alert("Delete failed");
      return;
    }

    fetchAdmins();
  };

  if (loading) return <div className="p-6 text-slate-500">Loading admins...</div>;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Admins</h1>
          <p className="mt-1 text-sm text-slate-500">
            Platform-only accounts for Super Admin and System Manager access.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700"
        >
          + Add Admin
        </button>
      </div>

      {error && !showModal && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
          {error}
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-6 py-3 text-left">Name</th>
              <th className="px-6 py-3 text-left">Email</th>
              <th className="px-6 py-3 text-left">Role</th>
              <th className="px-6 py-3 text-left">Scope</th>
              <th className="px-6 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {admins.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-10 text-center text-slate-400">
                  No platform admins found.
                </td>
              </tr>
            ) : (
              admins.map((admin) => (
                <tr key={admin.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-6 py-4 font-semibold text-slate-800">{admin.name}</td>
                  <td className="px-6 py-4 text-slate-500">{admin.email}</td>
                  <td className="px-6 py-4">
                    <span className="rounded-full bg-green-50 px-2.5 py-1 text-xs font-semibold text-green-700">
                      {admin.role_name || admin.role}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-slate-500">App only</td>
                  <td className="px-6 py-4 text-right">
                    <button onClick={() => openEdit(admin)} className="mr-3 text-sm font-semibold text-blue-600 hover:text-blue-800">
                      Edit
                    </button>
                    <button onClick={() => deleteAdmin(admin.id)} className="text-sm font-semibold text-red-500 hover:text-red-700">
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-5 flex items-center justify-between">
              <h2 className="text-lg font-bold text-slate-900">{editingId ? "Edit Admin" : "Create Admin"}</h2>
              <button onClick={closeModal} className="text-xl leading-none text-slate-400 hover:text-slate-700">x</button>
            </div>

            {error && (
              <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-600">
                {error}
              </div>
            )}

            <div className="space-y-3">
              <label className="block">
                <span className="mb-1 block text-xs font-semibold text-slate-500">Name</span>
                <input value={form.name} onChange={field("name")} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-green-200" />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-semibold text-slate-500">Email</span>
                <input type="email" value={form.email} onChange={field("email")} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-green-200" />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-semibold text-slate-500">Role</span>
                <select value={form.role_id} onChange={field("role_id")} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-green-200">
                  <option value="">Select admin role</option>
                  {adminRoles.map((role) => (
                    <option key={role.id} value={role.id}>{role.name}</option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-semibold text-slate-500">
                  Password {editingId && <span className="font-normal text-slate-400">(leave blank to keep current)</span>}
                </span>
                <input type="password" value={form.password} onChange={field("password")} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-green-200" />
              </label>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button onClick={closeModal} className="rounded-lg px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
              <button onClick={saveAdmin} disabled={saving} className="rounded-lg bg-green-600 px-5 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-60">
                {saving ? "Saving..." : editingId ? "Save Changes" : "Create Admin"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admins;
